import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconBrandReykjavik } from '@components/icons/brand-reykjavik';
import { IconSend } from '@components/icons/send';
import { InfoBox } from '@components/info-box';
import { IntegrationCard } from '@components/integration-card';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import { JobCaseType } from './api';
import { useHladanJobCase, useCreateJobCase } from './hooks';

export const HladanJobPage = () => {
  const { t } = useScopedTranslate(locale);
  const { lang } = useStore();
  const { jobID } = useParams();
  const [refetch, setRefetch] = useState(false);
  const onDataLoad = (data: JobCaseType) => {
    setRefetch(data.exists && !data.completed);
  };
  const caseQuery = useHladanJobCase(jobID ?? '', refetch, onDataLoad);
  const { showModal } = useModal();
  const uploadMutation = useCreateJobCase(jobID ?? '');
  const exists = Boolean(caseQuery.data?.exists);
  if (caseQuery.isLoading) {
    return <Spinner />;
  }
  const jobCase = caseQuery.data;
  const onSubmit = () => {
    // @ts-expect-error
    showModal('CONFIRM_MODAL', {
      confirm: (id: number) => {
        uploadMutation.mutate();
        return true;
      },
      data: {
        id: jobID,
      },
      buttonIcon: <IconSend />,
      headerText: t('sendApplications'),
      mainText: t('confirmDescription'),
      confirmText: t('send'),
    });
  };

  return (
    <Stack space={6} orientation="vertical">
      <IntegrationCard icon={IconBrandReykjavik} header="Hlaðan" text={t('sendApplications')} />
      <Card space={6}>
        <Stack space={6}>
          <Text font="bold16" text={t('applicantData')} />
          <Stack space={2}>
            <Text font="semibold14" text={t('dataForTransfer')} />
            <Text text={t('applicationInfo')} />
            <Text text={t('resume')} />
            <Text text={t('coverLetter')} />
            <Text text={t('applicationaAttachments')} />
          </Stack>
          {exists ? (
            <>
              <Separator />
              <Stack space={4}>
                <Stack space={1}>
                  <Text font="semibold14" text={t('createdBy')} />
                  <div>
                    <Text font="regular16" text={jobCase?.createdBy} />
                    {jobCase?.dateCreated && (
                      <Text text={abbreviateDate(jobCase?.dateCreated, lang)} />
                    )}
                  </div>
                </Stack>
                {!jobCase?.completed && (
                  <InfoBox
                    variant="indigo"
                    text={t('notCompleted', {
                      completed: jobCase?.completedApplications ?? 0,
                      total: jobCase?.totalApplications ?? 0,
                    })}
                  ></InfoBox>
                )}
              </Stack>
            </>
          ) : (
            <Stack align="flex-end" space={2}>
              <Button
                variant="primary"
                iconLeft={<IconSend />}
                text={t('send')}
                onClick={onSubmit}
                disabled={uploadMutation.isLoading}
              />
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

const locale = {
  en: {
    sendApplications: 'Send applications and attachments to the filing system',
    applicantData: 'Applicant data',
    dataForTransfer: 'Data for transfer',
    applicationInfo: 'Application information',
    resume: 'Resumé / CV',
    coverLetter: 'Cover letter',
    applicationaAttachments: 'Application attachments (if applicable)',
    send: 'Send',
    confirmDescription:
      'You are about to send all applications and attachments to the filing system.\nThis operation can only be performed once, so please make sure that there are no more applications expected.\nThis action can not be undone.',
    dataWillBeSent: 'Data will be sent to the filing system',
    errorOccured: 'An error occured. Please try again.',
    createdBy: 'Created by',
    notCompleted:
      'Transfer has not been completed. It might take a few of minutes.\n\n{completed} of {total} applications completed',
  },
  is: {
    sendApplications: 'Senda umsóknir og fylgigögn í skjalakerfi',
    applicantData: 'Umsóknamál',
    dataForTransfer: 'Gögn til flutnings',
    applicationInfo: 'Umsóknarupplýsingar',
    resume: 'Ferilskrá',
    coverLetter: 'Kynningarbréf',
    applicationaAttachments: 'Viðhengi á umsókn (ef við á)',
    send: 'Senda',
    confirmDescription:
      'Þú ert að fara að senda allar umsóknir og fylgigögn í skjalakerfi.\nÞessa aðgerð er einungis hægt að framkvæma einu sinni þannig vinsamlegast gangið úr skugga um að allar umsóknir séu komnar.\nÞessa aðgerð er ekki hægt að afturkalla.',
    dataWillBeSent: 'Gögn verða send í skjalakerfi',
    errorOccured: 'Óvænt villa kom upp. Vinsamlegast reynið aftur.',
    createdBy: 'Stofnað af',
    notCompleted:
      'Flutningi er ekki lokið. Það gæti tekið nokkrar mínútur.\n\n{completed} af {total} umsóknum lokið',
  },
};
